import { servicePages } from '../data/servicePages';

export const SITE_NAME = 'Codinity';
export const SITE_URL = 'https://www.codinity.net';

const DEFAULT_DESCRIPTION =
  'Codinity - AI & Automation, Custom Software, Web, Mobile, and Cloud Engineering';

// Static routes. Anything not listed here is resolved by matchers below.
const STATIC_ROUTES = {
  '/': {
    title: 'Codinity | Software Development, Mobile Apps & AI Solutions',
    description: DEFAULT_DESCRIPTION,
  },
  '/services': {
    title: 'Services | Codinity',
    description:
      'AI & automation, custom software, web development, mobile apps, and cloud engineering — the services Codinity builds and ships.',
  },
  '/careers': {
    title: 'Careers | Codinity',
    description:
      'Build your career with Codinity. See open engineering roles, how we work, and what life on the team looks like.',
  },
  '/blog': {
    title: 'Blog - Insights & Engineering | Codinity',
    description:
      'Engineering notes, product thinking, and practical guides from the team building software at Codinity.',
  },
  '/contact': {
    title: 'Contact | Codinity',
    description:
      'Tell us what you want to build. Free consultation, honest timelines, and a reply within one business day.',
  },
};

const NOT_FOUND = {
  title: `Page not found | ${SITE_NAME}`,
  description: DEFAULT_DESCRIPTION,
  noindex: true,
};

/**
 * Resolve the document metadata for a pathname.
 * Dynamic pages (a blog post) may refine the title once their data loads.
 */
export const metaForPath = (pathname = '/') => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  if (STATIC_ROUTES[path]) return STATIC_ROUTES[path];

  const serviceMatch = path.match(/^\/services\/([^/]+)$/);
  if (serviceMatch) {
    // Guard against inherited Object.prototype keys ("constructor", "toString", …)
    const service = Object.prototype.hasOwnProperty.call(servicePages, serviceMatch[1])
      ? servicePages[serviceMatch[1]]
      : null;
    if (service) {
      return {
        title: `${service.title} | ${SITE_NAME}`,
        description: service.description || service.tagline || DEFAULT_DESCRIPTION,
      };
    }
    return NOT_FOUND;
  }

  if (/^\/blog\/[^/]+$/.test(path)) {
    // BlogPost overrides the title once the post resolves; this is the interim value.
    return { title: `Article | ${SITE_NAME} Blog`, description: STATIC_ROUTES['/blog'].description };
  }

  return NOT_FOUND;
};

const setMetaTag = (selector, attr, value) => {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

const setRobots = (noindex) => {
  let el = document.head.querySelector('meta[name="robots"]');
  if (!noindex) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    document.head.appendChild(el);
  }
  el.setAttribute('content', 'noindex');
};

/**
 * Apply title / description / canonical for a pathname.
 * index.html ships a single hardcoded canonical pointing at the homepage, so
 * without this every route told search engines it *was* the homepage.
 */
export const applyPageMeta = (pathname) => {
  const { title, description, noindex } = metaForPath(pathname);

  document.title = title;
  setMetaTag('meta[name="description"]', 'content', description);
  setMetaTag('meta[property="og:title"]', 'content', title);
  setMetaTag('meta[property="og:description"]', 'content', description);
  setMetaTag('link[rel="canonical"]', 'href', `${SITE_URL}${pathname === '/' ? '/' : pathname}`);
  setRobots(Boolean(noindex));
};
